// 个人中心相关工具函数

import type { 
  MembershipLevelType, 
  PaymentAddressType, 
  NotificationType,
  SystemNotification,
  ProjectRecord
} from '../types/personal-center'
import { 
  MEMBERSHIP_LEVELS, 
  getNextMembershipLevel, 
  calculateUpgradeProgress
} from '../constants/membership'

// 通知类型配置
const NOTIFICATION_TYPES = {
  transaction: { label: 'Transaction', icon: 'credit-card', color: '#10B981' },
  recharge: { label: 'Recharge', icon: 'plus-circle', color: '#3B82F6' },
  withdraw: { label: 'Withdraw', icon: 'minus-circle', color: '#F59E0B' },
  system: { label: 'System', icon: 'bell', color: '#6B7280' },
  project: { label: 'Project', icon: 'briefcase', color: '#8B5CF6' },
  membership: { label: 'Membership', icon: 'star', color: '#F59E0B' }
}

// 收款地址类型配置
const PAYMENT_ADDRESS_TYPES = {
  bank: { label: 'Bank Account', icon: 'building-bank', color: '#3B82F6' },
  btc: { label: 'Bitcoin', icon: 'currency-bitcoin', color: '#F7931A' },
  eth: { label: 'Ethereum', icon: 'currency-ethereum', color: '#627EEA' }
}

// 密码规则配置
export const PASSWORD_RULES = {
  login_password: {
    min_length: 8,
    max_length: 50,
    require_uppercase: true,
    require_lowercase: true,
    require_numbers: true,
    require_special_chars: false
  },
  withdraw_pin: {
    min_length: 10,
    max_length: 10,
    require_uppercase: true,
    require_lowercase: true,
    require_numbers: true,
    min_numbers: 8
  },
  max_attempts: 3,
  lockout_duration: 30 // 分钟
}

/**
 * 格式化货币金额
 */
export function formatCurrency(amount: number, currency: string = 'SGD'): string {
  return new Intl.NumberFormat('en-SG', {
    style: 'currency',
    currency: currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(amount)
}

/**
 * 格式化碳信用数量
 */
export function formatCarbonCredits(amount: number): string {
  return new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(amount) + ' tCO₂e'
}

/**
 * 格式化百分比
 */
export function formatPercentage(value: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'percent',
    minimumFractionDigits: 1,
    maximumFractionDigits: 1
  }).format(value / 100)
}

/**
 * 获取会员等级显示信息
 */
export function getMembershipDisplayInfo(level: MembershipLevelType) {
  const levelInfo = MEMBERSHIP_LEVELS[level]
  return {
    ...levelInfo,
    displayName: `${level} - ${levelInfo.title}`,
    badgeStyle: {
      background: `linear-gradient(135deg, ${levelInfo.badge_gradient.join(', ')})`,
      color: '#ffffff',
      textShadow: '0 1px 2px rgba(0,0,0,0.3)'
    }
  }
}

/**
 * 计算会员升级信息
 */
export function calculateMembershipUpgrade(
  currentLevel: MembershipLevelType,
  currentInvestment: number,
  currentCarbonCredits: number
) {
  const nextLevel = getNextMembershipLevel(currentLevel)
  if (!nextLevel) {
    return {
      canUpgrade: false,
      progress: 100,
      nextLevel: null,
      requirements: null
    }
  }

  const nextLevelInfo = MEMBERSHIP_LEVELS[nextLevel.level]
  const progress = calculateUpgradeProgress(currentLevel, currentInvestment, currentCarbonCredits)
  
  const investmentNeeded = Math.max(0, nextLevelInfo.requirements.min_investment - currentInvestment)
  const creditsNeeded = Math.max(0, nextLevelInfo.requirements.min_carbon_credits - currentCarbonCredits)

  return {
    canUpgrade: progress >= 100,
    progress,
    nextLevel: nextLevelInfo,
    requirements: {
      investmentNeeded,
      creditsNeeded,
      investmentMet: investmentNeeded === 0,
      creditsMet: creditsNeeded === 0
    }
  }
}

/**
 * 获取通知类型显示信息
 */
export function getNotificationTypeInfo(type: NotificationType) {
  return NOTIFICATION_TYPES[type] || NOTIFICATION_TYPES.system
}

/**
 * 格式化通知时间
 */
export function formatNotificationTime(dateString: string): string {
  const date = new Date(dateString)
  const now = new Date()
  const diffInMinutes = Math.floor((now.getTime() - date.getTime()) / (1000 * 60))

  if (diffInMinutes < 1) {
    return 'Just now'
  } else if (diffInMinutes < 60) {
    return `${diffInMinutes} minutes ago`
  } else if (diffInMinutes < 24 * 60) {
    const hours = Math.floor(diffInMinutes / 60)
    return `${hours} hour${hours > 1 ? 's' : ''} ago`
  } else if (diffInMinutes < 7 * 24 * 60) {
    const days = Math.floor(diffInMinutes / (24 * 60))
    return `${days} day${days > 1 ? 's' : ''} ago`
  } else {
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    })
  }
}

/**
 * 对通知进行分组
 */
export function groupNotificationsByDate(notifications: SystemNotification[]) {
  const groups: Record<string, SystemNotification[]> = {}
  
  notifications.forEach(notification => {
    const date = new Date(notification.created_at)
    const today = new Date()
    const yesterday = new Date(today)
    yesterday.setDate(yesterday.getDate() - 1)
    
    let groupKey: string
    if (date.toDateString() === today.toDateString()) {
      groupKey = 'Today'
    } else if (date.toDateString() === yesterday.toDateString()) {
      groupKey = 'Yesterday'
    } else {
      groupKey = date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      })
    }
    
    if (!groups[groupKey]) {
      groups[groupKey] = []
    }
    groups[groupKey].push(notification)
  })
  
  return groups
}

/**
 * 获取收款地址类型信息
 */
export function getPaymentAddressTypeInfo(type: PaymentAddressType) {
  return PAYMENT_ADDRESS_TYPES[type]
}

/**
 * 验证收款地址格式
 */
export function validatePaymentAddress(type: PaymentAddressType, address: string): {
  valid: boolean
  error?: string
} {
  switch (type) {
    case 'btc':
      // Bitcoin地址验证 (简化版)
      if (!/^[13][a-km-zA-HJ-NP-Z1-9]{25,34}$|^bc1[a-z0-9]{39,59}$/.test(address)) {
        return { valid: false, error: 'Invalid Bitcoin address format' }
      }
      break
    case 'eth':
      // Ethereum地址验证
      if (!/^0x[a-fA-F0-9]{40}$/.test(address)) {
        return { valid: false, error: 'Invalid Ethereum address format' }
      }
      break
    case 'bank':
      // 银行账户验证 (基本长度检查)
      if (address.length < 8 || address.length > 20) {
        return { valid: false, error: 'Bank account number should be 8-20 characters' }
      }
      break
  }
  
  return { valid: true }
}

/**
 * 验证提现密码格式
 */
export function validateWithdrawPin(pin: string): {
  valid: boolean
  errors: string[]
} {
  const errors: string[] = []
  
  if (pin.length !== 10) {
    errors.push('Password must be exactly 10 characters')
  }
  
  if (!/[A-Z]/.test(pin)) {
    errors.push('Password must contain at least 1 uppercase letter')
  }
  
  if (!/[a-z]/.test(pin)) {
    errors.push('Password must contain at least 1 lowercase letter')
  }
  
  const digitCount = (pin.match(/\d/g) || []).length
  if (digitCount < 8) {
    errors.push('Password must contain at least 8 digits')
  }
  
  if (!/^[A-Za-z\d]+$/.test(pin)) {
    errors.push('Password can only contain letters and numbers')
  }
  
  return {
    valid: errors.length === 0,
    errors
  }
}

/**
 * 计算提现手续费
 */
export function calculateWithdrawFee(amount: number, feePercentage: number = 2): {
  fee: number
  netAmount: number
  feePercentage: number
} {
  // 先计算精确的费用和净额
  const rawFee = amount * (feePercentage / 100)
  const rawNetAmount = amount - rawFee

  // 最后统一四舍五入
  const fee = Math.round(rawFee * 100) / 100
  const netAmount = Math.round(rawNetAmount * 100) / 100

  return {
    fee,
    netAmount,
    feePercentage
  }
}

/**
 * 格式化项目状态
 */
export function formatProjectStatus(status: ProjectRecord['status']): {
  label: string
  color: string
  bgColor: string
} {
  const statusMap = {
    ongoing: {
      label: 'Ongoing',
      color: '#10B981',
      bgColor: '#D1FAE5'
    },
    completed: {
      label: 'Completed',
      color: '#3B82F6',
      bgColor: '#DBEAFE'
    },
    pending_payment: {
      label: 'Pending Payment',
      color: '#F59E0B',
      bgColor: '#FEF3C7'
    },
    cancelled: {
      label: 'Cancelled',
      color: '#EF4444',
      bgColor: '#FEE2E2'
    }
  }
  
  return statusMap[status] || statusMap.ongoing
}

/**
 * 计算项目投资回报率
 */
export function calculateProjectROI(
  investmentAmount: number,
  currentValue: number
): {
  roi: number
  roiPercentage: string
  profit: number
  isProfit: boolean
} {
  const profit = currentValue - investmentAmount
  const roi = investmentAmount > 0 ? (profit / investmentAmount) * 100 : 0
  
  return {
    roi,
    roiPercentage: formatPercentage(roi),
    profit,
    isProfit: profit > 0
  }
}

/**
 * 生成随机参考号
 */
export function generateReferenceNumber(prefix: string = 'REF'): string {
  const timestamp = Date.now().toString(36)
  const random = Math.random().toString(36).substring(2, 8)
  return `${prefix}-${timestamp}-${random}`.toUpperCase()
}

/**
 * 检查是否为工作日
 */
export function isBusinessDay(date: Date = new Date()): boolean {
  const day = date.getDay()
  return day >= 1 && day <= 5 // 周一到周五
}

/**
 * 计算预计处理时间
 */
export function calculateEstimatedProcessingTime(
  businessDays: number = 3,
  fromDate: Date = new Date()
): Date {
  const result = new Date(fromDate)
  let addedDays = 0
  
  while (addedDays < businessDays) {
    result.setDate(result.getDate() + 1)
    if (isBusinessDay(result)) {
      addedDays++
    }
  }
  
  return result
}

export function safeMultiply(a: number, b: number): number {
  return Math.round(a * b * 100) / 100
}

export function formatPrice(price: number): string {
  return safeMultiply(price, 1).toFixed(2)
}